import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useWorkOrderData } from '../../../../context/WorkOrdersContext';
import { WorkOrderFilterCategory } from '../../../../enums/workOrderFilter';
import AssetManagerService from '../../../../services/AssetManager/assetManager.service';
import type { AssetManagerLayer } from '../../../../types/InfrastructureManagerTypes';

type ValueOption = { value: string; label: string };

const NO_LAYERS: AssetManagerLayer[] = [];

// One option per id, sorted by label.
const toOptions = (items: { id: number; name: string }[]): ValueOption[] =>
  [...new Map(items.map((item) => [item.id, item])).values()]
    .map(({ id, name }) => ({ value: String(id), label: name }))
    .sort((a, b) => a.label.localeCompare(b.label));

// Value choices for the non-date categories: the asset map's layers, and the contacts and
// submitters found on the loaded work orders (so every choice can match something).
export const useFilterValueOptions = (): Partial<Record<WorkOrderFilterCategory, ValueOption[]>> => {
  const { orders } = useWorkOrderData();
  const { getMetaLayers } = AssetManagerService();
  const { data: layers = NO_LAYERS } = useQuery(getMetaLayers());

  return useMemo(
    () => ({
      [WorkOrderFilterCategory.LAYER]: toOptions(layers),
      [WorkOrderFilterCategory.ASSIGNED_TO]: toOptions(orders.flatMap((order) => order.contacts)),
      [WorkOrderFilterCategory.SUBMITTED_BY]: toOptions(orders.flatMap((order) => (order.createdBy ? [order.createdBy] : []))),
    }),
    [orders, layers],
  );
};
